import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useUserProfile } from "@/hooks/use-user-profiles";
import { cn } from "@/lib/utils";

interface UserAvatarProps {
  userId?: string | null;
  displayName?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
  className?: string;
  fallbackClassName?: string;
  size?: "micro" | "xs" | "sm" | "md" | "lg" | "xl";
  /**
   * Inline style for the fallback. Inline values win over the class list, so
   * `backgroundImage: "none"` is needed to drop the default gradient before a
   * flat `backgroundColor` can show (guest identity colours use this).
   */
  fallbackStyle?: React.CSSProperties;
  /**
   * Draw the `primary/20` ring around the avatar. This was destructured and
   * used from the start but never declared on the interface, so every call
   * site passing it was a type error — the prop did not exist as far as TS
   * knew, and the ring silently fell out of the type.
   */
  showBorder?: boolean;
}

const sizeClasses = {
    micro: "h-3.5 w-3.5",
    xs: "h-6 w-6",
    sm: "h-7 w-7",
    md: "h-8 w-8",
    lg: "h-9 w-9",
    xl: "h-10 w-10",
};

const fallbackFontSizes = {
    micro: "text-[5px]",
    xs: "text-[11px]",
    sm: "text-[11px]",
    md: "text-[11px]",
    lg: "text-xs",
    xl: "text-sm",
};

const getInitials = (email: string, displayName?: string | null): string => {
  if (displayName) {
    return displayName.trim()[0]?.toUpperCase() || "?";
  }
  return email.split("@")[0][0]?.toUpperCase() || "?";
};

export const UserAvatar = ({
  userId,
  displayName: propDisplayName,
  email: propEmail,
  avatarUrl: propAvatarUrl,
  className,
  fallbackClassName,
  fallbackStyle,
  size = "md",
  showBorder = false,
}: UserAvatarProps) => {
  // Fetch profile if userId is provided and we don't have displayName/avatarUrl
  const shouldFetchProfile = userId && (!propDisplayName && !propAvatarUrl);
  const { data: profile } = useUserProfile(shouldFetchProfile ? userId : null);

  // Use props first, then profile data, then fallback
  const displayName = propDisplayName || profile?.display_name || null;
  const email = propEmail || profile?.email || null;
  const avatarUrl = propAvatarUrl || profile?.avatar_url || null;

  // Generate initials
  const initials = email
    ? getInitials(email, displayName)
    : displayName
      ? displayName.substring(0, 2).toUpperCase()
      : "?";

  const sizeClass = sizeClasses[size];
  const borderClass = showBorder ? "border-2 border-primary/20" : "";

  const fallbackFontSize = fallbackFontSizes[size];

  return (
    <Avatar className={cn(sizeClass, borderClass, className)}>
      <AvatarImage src={avatarUrl || undefined} alt={displayName || email || "User"} />
      <AvatarFallback
        className={cn(
          "bg-gradient-to-br from-primary/20 to-secondary/20 text-foreground font-semibold",
          fallbackFontSize,
          fallbackClassName
        )}
        style={fallbackStyle}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
};

